import React from "react";
import { message as antdMessage, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
  fetchMatterDetail,
  fetchMatterDetailList,
  moveDocumentToFolder,
  moveDocumentsToFolder,
  optimisticFolderMove,
  reconcileFolderMove,
  restoreCategoryMoveSnapshot,
} from "../../slice/MatterDetailsSlice";

// Sentinel destination meaning "the matter root" (documents with no folder).
export const ROOT_DESTINATION = "root";

// Resolve the TRUE matter-root id. `matterDetails.matterDetails` is NOT safe
// for this: `fetchMatterDetailsFolder.fulfilled` overwrites it with
// `{...folderData, folder: true}` while the user is viewing inside a folder, so
// `.id` becomes the FOLDER id. `matterDetails.matterDetailsTree` is the full
// matter tree and is set ONLY by `fetchMatterDetail` (never by folder fetches),
// so its `.id` is the reliable matter id even from inside a folder. Without
// this, the post-move `fetchMatterDetail(...)` refetched the wrong resource
// and the moved document never visibly relocated in the sidebar tree.
// (Canonical home of this selector; DocumentContextMenu re-exports it.)
export const selectCurrentMatterId = (state) => {
  const slice = state?.matterDetails;
  if (!slice) return null;
  const treeMatterId = slice.matterDetailsTree?.id;
  if (treeMatterId != null) return treeMatterId;
  // Fallback: matterDetails.id is only trustworthy when not folder-contaminated.
  if (slice.matterDetails && slice.matterDetails.folder !== true) {
    return slice.matterDetails.id ?? null;
  }
  return null;
};

const stripPrefix = (value, prefix) =>
  typeof value === "string" && value.startsWith(prefix)
    ? value.slice(prefix.length)
    : value;

// Tree nodes carry the BE row on `record`; keys are unique_ids ("D<id>" /
// "F<id>"). Prefer the record's raw id, fall back to stripping the key.
export const getDocumentIdFromNode = (node) => {
  const record = node?.record || {};
  const raw =
    record.id ??
    record.document_id ??
    stripPrefix(typeof node?.key === "string" ? node.key : null, "D");
  return raw != null && raw !== "" ? String(raw) : null;
};

export const getFolderIdFromNode = (node) => {
  const record = node?.record || {};
  const raw =
    record.id ??
    stripPrefix(typeof node?.key === "string" ? node.key : null, "F");
  return raw != null && raw !== "" ? String(raw) : null;
};

export const getNodeDisplayName = (node) =>
  node?.record?.name ||
  (typeof node?.title === "string" ? node.title : "") ||
  "";

const getSubfolders = (folder) =>
  Array.isArray(folder?.subfolders) ? folder.subfolders : folder?.folders || [];

// Build antd TreeSelect treeData for the "Move to folder" modal from the raw
// matterDetailsTree payload. The document's current location is selectable in
// the picker but disabled (so the user can see where the file lives today).
// Optimistic placeholder folders (id == null, mid-flight creations from the
// categorize path) are skipped — they aren't valid move destinations yet.
export const buildFolderDestinationOptions = (tree, currentFolderId) => {
  const isCurrent = (folderId) =>
    currentFolderId != null &&
    currentFolderId !== "" &&
    String(folderId) === String(currentFolderId);

  const visit = (folders) =>
    (Array.isArray(folders) ? folders : [])
      .filter((folder) => folder && folder.id != null)
      .map((folder) => ({
        value: String(folder.id),
        title: isCurrent(folder.id)
          ? `${folder.name || "(unnamed folder)"} (current location)`
          : folder.name || "(unnamed folder)",
        disabled: isCurrent(folder.id),
        children: visit(getSubfolders(folder)),
      }));

  const documentIsAtRoot = currentFolderId == null || currentFolderId === "";
  return [
    {
      value: ROOT_DESTINATION,
      title: documentIsAtRoot
        ? "Matter files (current location)"
        : "Matter files (top level)",
      disabled: documentIsAtRoot,
      children: [],
    },
    ...visit(tree?.folders || []),
  ];
};

// One shared move flow for every surface (tree drag-and-drop, "Move to
// folder" modal, future bulk move): snapshot → optimistic tree move → API →
// reconcile with the authoritative payload (or roll the snapshot back) →
// background refetch of tree + list. Mirrors CategorizeDocumentModal's
// optimistic recategorize cycle, minus anything category-related.
//
// Returns { ok, moved, message } so modal callers can decide close/keep-open;
// user-facing toasts are fired here so drag-and-drop (which has no modal to
// report through) gets identical feedback.
export const performDocumentMove =
  ({
    documentId,
    documentName = "",
    destinationFolderId,
    destinationName = "",
    matterId,
    documentRecord,
    onConflict,
  }) =>
  async (dispatch, getState) => {
    const state = getState() || {};
    const slice = state.matterDetails || {};
    const currentFolderId = state.websocket?.currentFolderId;
    const toRoot =
      destinationFolderId == null ||
      destinationFolderId === "" ||
      destinationFolderId === ROOT_DESTINATION;
    const normalizedDestination = toRoot ? null : String(destinationFolderId);

    const snapshot = JSON.parse(
      JSON.stringify({
        matterDetailsTree: slice.matterDetailsTree,
        matterDetailsList: slice.matterDetailsList,
        updateMeta: slice.updateMeta,
        documentDetail: slice.documentDetail,
        totalRecords: slice.totalRecords,
      })
    );

    dispatch(
      optimisticFolderMove({
        documentId: String(documentId),
        destinationFolderId: normalizedDestination,
        currentFolderId,
        document: documentRecord,
      })
    );

    const action = await dispatch(
      moveDocumentToFolder({
        documentId: String(documentId),
        folderId: normalizedDestination,
        onConflict,
      })
    );

    if (moveDocumentToFolder.fulfilled.match(action)) {
      const payload = action.payload || {};
      dispatch(
        reconcileFolderMove({
          documentId: String(documentId),
          destinationFolderId: normalizedDestination,
          destinationFolder: payload.destination_folder,
          document: payload.document,
          currentFolderId,
        })
      );

      const destinationLabel =
        payload.destination_folder?.name ||
        destinationName ||
        (toRoot ? "the matter files" : "the selected folder");
      const docLabel = documentName || "Document";
      if (payload.moved === false) {
        antdMessage.info(`"${docLabel}" is already in ${destinationLabel}.`);
      } else if (payload.renamed_to) {
        antdMessage.success(
          `Moved to ${destinationLabel} as "${payload.renamed_to}" (a file with the original name was already there).`
        );
      } else {
        antdMessage.success(`Moved "${docLabel}" to ${destinationLabel}.`);
      }

      if (matterId) {
        dispatch(fetchMatterDetail(matterId));
        // Keep the document table scoped to the folder the user still has
        // open: websocket.currentFolderId is the URL-derived folder id
        // stripped of its "F" prefix ("root" at the matter root, null
        // inside a document), and the list endpoint takes the F-prefixed
        // unique id — same convention as TabView's list refetches.
        const listArgs = {
          id: matterId,
          page: slice.currentPage || 1,
          sort_direction: slice.sort,
          sort_by: slice.sortBy,
        };
        if (currentFolderId && currentFolderId !== "root") {
          const rawFolder = String(currentFolderId);
          listArgs.folder = rawFolder.startsWith("F")
            ? rawFolder
            : `F${rawFolder}`;
        }
        dispatch(fetchMatterDetailList(listArgs));
      }
      return { ok: true, moved: payload.moved !== false };
    }

    // restoreCategoryMoveSnapshot is a generic tree/list/meta snapshot
    // restorer despite the name — safe to reuse for folder moves.
    dispatch(restoreCategoryMoveSnapshot(snapshot));
    const status = action?.payload?.status;
    const message =
      action?.payload?.message || "Couldn't move the document — try again.";
    // A 409 is a NAME COLLISION, not a failure. Signal it to the caller
    // (drag-drop handler) so it can offer "Keep both" instead of showing
    // a dead error — but only when we haven't already tried renaming.
    if (status === 409 && onConflict !== "rename") {
      return {
        ok: false,
        moved: false,
        conflict: true,
        message,
        documentName,
        destinationName,
      };
    }
    antdMessage.error(message);
    return { ok: false, moved: false, message };
  };

// Shared refetch used by bulk flows: one tree refresh + one list refresh
// scoped to whatever folder the user still has open.
const refetchTreeAndList = (dispatch, getState, matterId) => {
  if (!matterId) return;
  const state = getState() || {};
  const slice = state.matterDetails || {};
  const currentFolderId = state.websocket?.currentFolderId;
  dispatch(fetchMatterDetail(matterId));
  const listArgs = {
    id: matterId,
    page: slice.currentPage || 1,
    sort_direction: slice.sort,
    sort_by: slice.sortBy,
  };
  if (currentFolderId && currentFolderId !== "root") {
    const rawFolder = String(currentFolderId);
    listArgs.folder = rawFolder.startsWith("F") ? rawFolder : `F${rawFolder}`;
  }
  dispatch(fetchMatterDetailList(listArgs));
};

// Names already taken in a move destination, read straight out of the
// matter tree the sidebar is rendering. This is what lets the "same names
// are already there" dialog appear on the drop instead of after a round
// trip per file.
//
// Returns null when the destination isn't in the tree we hold — "don't
// know", so predict nothing and let the server be the judge.
//
// The comparison is case-SENSITIVE because the server's duplicate check is
// (`name=document.name`). Guessing case-insensitively here would raise the
// dialog over files the server would have let through untouched.
export const collectNamesInDestination = (
  rawTree,
  destinationFolderId,
  excludeDocumentIds = []
) => {
  const excluded = new Set((excludeDocumentIds || []).map(String));
  const namesFrom = (docs) => {
    const names = new Set();
    for (const doc of Array.isArray(docs) ? docs : []) {
      if (!doc || !doc.name) continue;
      // A file being moved can't collide with itself.
      if (doc.id != null && excluded.has(String(doc.id))) continue;
      names.add(doc.name);
    }
    return names;
  };

  const toRoot =
    destinationFolderId == null ||
    destinationFolderId === "" ||
    destinationFolderId === ROOT_DESTINATION;
  if (toRoot) {
    return rawTree ? namesFrom(rawTree.documents) : null;
  }

  const wanted = String(destinationFolderId);
  const find = (folders) => {
    for (const folder of Array.isArray(folders) ? folders : []) {
      if (!folder || typeof folder !== "object") continue;
      if (folder.id != null && String(folder.id) === wanted) return folder;
      const nested = find(getSubfolders(folder));
      if (nested) return nested;
    }
    return null;
  };
  const destination = find(rawTree?.folders);
  return destination ? namesFrom(destination.documents) : null;
};

// Split a selection into the files whose names are free in the destination
// and the files that would collide. `takenNames` of null means we couldn't
// see the destination, so nothing is predicted.
export const partitionByNameCollision = (documents, takenNames) => {
  if (!takenNames) return { clean: documents, clashing: [] };
  const clean = [];
  const clashing = [];
  // Two files with the same name inside ONE selection collide with each
  // other as well: whichever lands first takes the name.
  const claimedInBatch = new Set();
  for (const doc of documents) {
    const name = doc.documentName || "";
    if (name && (takenNames.has(name) || claimedInBatch.has(name))) {
      clashing.push(doc);
    } else {
      clean.push(doc);
    }
    if (name) claimedInBatch.add(name);
  }
  return { clean, clashing };
};

const keepBothDialogCopy = (conflicts, where) => {
  const names = conflicts
    .map((d) => d.documentName || "a file")
    .slice(0, 5)
    .join(", ");
  return {
    title:
      conflicts.length === 1
        ? "A file with that name is already there"
        : `${conflicts.length} files with the same names are already there`,
    content: `${names}${conflicts.length > 5 ? ", …" : ""} already exist${conflicts.length === 1 ? "s" : ""} in ${where}. Keep both — the moved copies will be renamed (e.g. "… (2)") — or cancel and leave them where they are.`,
  };
};

// One batch: optimistic placement for the whole group, ONE request, then
// reconcile from the authoritative payloads. No per-file tree snapshot —
// the caller's single refetch is what corrects anything that didn't land,
// which also spares a big matter eleven deep clones of its own tree.
const runMoveBatch =
  ({ documents, destinationFolderId, onConflict }) =>
  async (dispatch, getState) => {
    const currentFolderId = getState()?.websocket?.currentFolderId;
    const toRoot =
      destinationFolderId == null ||
      destinationFolderId === "" ||
      destinationFolderId === ROOT_DESTINATION;
    const normalizedDestination = toRoot ? null : String(destinationFolderId);

    for (const doc of documents) {
      dispatch(
        optimisticFolderMove({
          documentId: String(doc.documentId),
          destinationFolderId: normalizedDestination,
          currentFolderId,
          document: doc.record || undefined,
        })
      );
    }

    const action = await dispatch(
      moveDocumentsToFolder({
        documentIds: documents.map((doc) => String(doc.documentId)),
        folderId: normalizedDestination,
        onConflict,
      })
    );

    if (!moveDocumentsToFolder.fulfilled.match(action)) {
      return {
        ok: false,
        moved: 0,
        renamed: 0,
        conflicts: [],
        failed: documents.length,
        message: action?.payload?.message,
      };
    }

    const payload = action.payload || {};
    const moved = Array.isArray(payload.moved) ? payload.moved : [];
    for (const entry of moved) {
      dispatch(
        reconcileFolderMove({
          documentId: String(entry.id),
          destinationFolderId: normalizedDestination,
          destinationFolder: entry.destination_folder,
          document: entry.document,
          currentFolderId,
        })
      );
    }

    const byId = new Map(
      documents.map((doc) => [String(doc.documentId), doc])
    );
    const conflicts = (
      Array.isArray(payload.conflicts) ? payload.conflicts : []
    )
      .map((entry) => byId.get(String(entry.id)))
      .filter(Boolean);

    return {
      ok: true,
      moved: moved.length,
      renamed: moved.filter((entry) => entry.renamed_to).length,
      conflicts,
      failed: (Array.isArray(payload.failed) ? payload.failed : []).length,
    };
  };

// Move several documents into one destination (multi-select drag).
//
// The whole selection goes in ONE request, and the collision question is
// asked from what the tree already knows rather than from what the server
// says: dragging eleven files used to mean eleven round trips before the
// dialog appeared, and eleven more after "Keep both" (Matthew, 2026-09-21).
//
// "rename" only suffixes names that really are taken, so confirming "Keep
// both" can re-send the entire selection as one batch — the files with free
// names are untouched by it. That keeps the two groups in a single request
// and a single ordering, which matters when the duplicate names are inside
// the selection itself.
export const performBulkDocumentMove =
  ({ documents, destinationFolderId, destinationName = "", matterId }) =>
  async (dispatch, getState) => {
    const queue = (documents || []).filter((doc) => doc && doc.documentId);
    if (queue.length === 0) return { ok: false, moved: 0 };

    const where = destinationName || "the selected folder";
    const finish = () => refetchTreeAndList(dispatch, getState, matterId);

    const moveGroup = (group, onConflict) =>
      dispatch(runMoveBatch({ documents: group, destinationFolderId, onConflict }));

    const report = (outcome) => {
      // ok === false means the request itself never landed; per-document
      // problems come back as a `failed` count on a successful request.
      if (!outcome.ok) {
        antdMessage.error(
          outcome.message || `Couldn't move the files to ${where} — try again.`
        );
      } else if (outcome.moved > 0 && outcome.failed === 0 && outcome.conflicts.length === 0) {
        const renamedNote = outcome.renamed > 0 ? " (renamed to avoid conflicts)" : "";
        antdMessage.success(
          outcome.moved === 1
            ? `Moved 1 file to ${where}${renamedNote}.`
            : `Moved ${outcome.moved} files to ${where}${renamedNote}.`
        );
      } else if (outcome.moved > 0 || outcome.failed > 0) {
        const parts = [];
        if (outcome.moved > 0) parts.push(`${outcome.moved} moved`);
        if (outcome.failed > 0) parts.push(`${outcome.failed} failed`);
        if (outcome.conflicts.length > 0) {
          parts.push(
            `${outcome.conflicts.length} name conflict${outcome.conflicts.length > 1 ? "s" : ""}`
          );
        }
        antdMessage[outcome.failed > 0 ? "warning" : "success"](
          `Move to ${where}: ${parts.join(", ")}.`
        );
      }
    };

    const runAndReport = async (group, onConflict) => {
      if (group.length === 0) return null;
      const outcome = await moveGroup(group, onConflict);
      finish();
      report(outcome);
      return outcome;
    };

    const takenNames = collectNamesInDestination(
      getState()?.matterDetails?.matterDetailsTree,
      destinationFolderId,
      queue.map((doc) => doc.documentId)
    );
    const { clean, clashing } = partitionByNameCollision(queue, takenNames);

    if (clashing.length > 0) {
      Modal.confirm({
        ...keepBothDialogCopy(clashing, where),
        icon: <ExclamationCircleOutlined />,
        okText: "Keep both",
        cancelText: "Cancel",
        onOk: () => runAndReport(queue, "rename"),
        // Cancel still moves the files whose names are free — the question
        // was only ever about the ones that clash.
        onCancel: () => runAndReport(clean, "error"),
      });
      return { ok: true, moved: 0, conflicts: clashing.length };
    }

    const outcome = await runAndReport(queue, "error");
    // A name the tree couldn't see (someone else's upload since it loaded).
    if (outcome?.conflicts.length > 0) {
      Modal.confirm({
        ...keepBothDialogCopy(outcome.conflicts, where),
        icon: <ExclamationCircleOutlined />,
        okText: "Keep both",
        cancelText: "Cancel",
        onOk: () => runAndReport(outcome.conflicts, "rename"),
      });
    }

    return {
      ok: (outcome?.failed ?? 0) === 0,
      moved: outcome?.moved ?? 0,
      conflicts: outcome?.conflicts.length ?? 0,
    };
  };

// Locate the nearest ancestor FOLDER node of `targetKey` inside the antd
// treeData (the converted nodes, not the raw BE payload). Returns
// { found, parent } where parent === null means "matter root". Nodes without
// a key (the `[{}]` placeholder children convertData emits for empty
// folders) can never be matched — callers must bail on keyless drop nodes
// BEFORE walking, or a placeholder could shadow the real target.
const findParentFolderNode = (nodes, targetKey, parent = null) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    if (!node || typeof node !== "object") continue;
    if (node.key != null && node.key === targetKey) {
      return { found: true, parent };
    }
    const nextParent = node.type === "folder" ? node : parent;
    const result = findParentFolderNode(node.children, targetKey, nextParent);
    if (result.found) return result;
  }
  return { found: false, parent: null };
};

const findNodeByKey = (nodes, targetKey) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    if (!node || typeof node !== "object") continue;
    if (node.key != null && node.key === targetKey) return node;
    const nested = findNodeByKey(node.children, targetKey);
    if (nested) return nested;
  }
  return null;
};

const resolveDestinationFromNode = (node, treeData) => {
  if (node?.type === "folder") {
    return {
      folderId: getFolderIdFromNode(node),
      folderName: getNodeDisplayName(node),
    };
  }
  if (node?.type === "document" && node.key != null) {
    const { found, parent } = findParentFolderNode(treeData, node.key);
    if (!found) return undefined;
    if (!parent) {
      // Top-level document: its container is the matter root.
      return { folderId: null, folderName: "" };
    }
    return {
      folderId: getFolderIdFromNode(parent),
      folderName: getNodeDisplayName(parent),
    };
  }
  return undefined;
};

// ── Custom drag-and-drop layer ──────────────────────────────────────────
//
// We deliberately do NOT use antd Tree's built-in drag-and-drop. Two
// generations of this feature died on rc-tree internals:
//   v1: rc-tree's calcDropPosition is reorder-oriented — a collapsed folder
//       only yields "drop inside" after ~1.5 indent-units of rightward
//       pointer travel, so allowDrop({dropPosition: 0}) filters vetoed
//       nearly every real drop (no highlight, no onDrop).
//   v2: worse — rc-tree only attaches dragenter/dragover/drop handlers to
//       nodes where nodeDraggable() is true (rc-tree 5.13 TreeNode.js:
//       `onDrop: isDraggable ? onDrop : undefined`). With only documents
//       draggable, FOLDER rows are drop dead-zones: hovering them fires
//       nothing at all. The library structurally cannot express
//       "documents drag, folders receive".
//
// So the tree renders plain rows (no Tree draggable prop at all) and this
// module implements native HTML5 DnD:
//   - document title spans get draggable + startDocumentDrag (payload
//     carried in dataTransfer under DOCUMENT_DRAG_MIME),
//   - ONE delegated dragover/drop pair on the tree's container resolves
//     the row under the pointer via data-tree-node-key and moves the file.
// Intent semantics: folder row -> into that folder; document row -> into
// that document's containing folder (matter root for top-level documents);
// untagged whitespace -> ignored.

export const DOCUMENT_DRAG_MIME = "application/x-dms-document-move";

// dataTransfer.types is a frozen array in modern browsers and a
// DOMStringList (contains(), no includes()) in older ones.
export const isDocumentDragEvent = (event) => {
  const types = event?.dataTransfer?.types;
  if (!types) return false;
  if (typeof types.includes === "function") {
    return types.includes(DOCUMENT_DRAG_MIME);
  }
  if (typeof types.contains === "function") {
    return types.contains(DOCUMENT_DRAG_MIME);
  }
  return Array.from(types).includes(DOCUMENT_DRAG_MIME);
};

// Flatten the CONVERTED tree nodes and return the document nodes whose keys
// are in the current selection. Used to turn a drag that starts on a
// selected row into a multi-document drag.
export const getSelectedDocumentNodes = (treeData, selectedKeys) => {
  const wanted = new Set((selectedKeys || []).map(String));
  if (wanted.size === 0) return [];
  const found = [];
  const visit = (nodes) => {
    for (const node of Array.isArray(nodes) ? nodes : []) {
      if (!node || typeof node !== "object") continue;
      if (node.type === "document" && node.key != null && wanted.has(String(node.key))) {
        found.push(node);
      }
      visit(node.children);
    }
  };
  visit(treeData);
  return found;
};

// Walk the RAW matter tree payload (matterDetailsTree: nested folders[] /
// documents[]) and return {id, name} for every document whose unique_id is
// in the given set. Used by the context menu's bulk Copy, whose selection
// keys are document unique_ids.
export const collectDocumentsByUniqueIds = (rawTree, uniqueIds) => {
  const wanted = new Set((uniqueIds || []).map(String));
  if (wanted.size === 0) return [];
  const found = [];
  const visitDocs = (docs) => {
    for (const doc of Array.isArray(docs) ? docs : []) {
      if (doc && doc.unique_id != null && wanted.has(String(doc.unique_id))) {
        found.push({ id: String(doc.id), name: doc.name || "" });
      }
    }
  };
  const visitFolders = (folders) => {
    for (const folder of Array.isArray(folders) ? folders : []) {
      if (!folder || typeof folder !== "object") continue;
      visitDocs(folder.documents);
      visitFolders(getSubfolders(folder));
    }
  };
  visitDocs(rawTree?.documents);
  visitFolders(rawTree?.folders);
  return found;
};

const dragEntryFromNode = (node) => ({
  documentId: getDocumentIdFromNode(node),
  documentName: getNodeDisplayName(node),
  folderId: node?.record?.folder_id ?? node?.record?.folder ?? null,
  record: node?.record || null,
});

// dragstart on a document title span. Returns true when a drag payload was
// installed; callers should preventDefault when it returns false so a
// half-configured row can't start a payload-less drag.
//
// When the dragged row is part of the current multi-selection, the payload
// carries EVERY selected document (multi-select move request, 2026-07-22);
// dragging an unselected row moves just that file, matching Explorer/Finder.
export const startDocumentDrag = ({ event, node, selectedDocumentNodes }) => {
  if (node?.type !== "document" || !event?.dataTransfer) return false;
  const documentId = getDocumentIdFromNode(node);
  if (!documentId) return false;

  const selection = Array.isArray(selectedDocumentNodes)
    ? selectedDocumentNodes.filter((n) => getDocumentIdFromNode(n))
    : [];
  const draggedIsSelected = selection.some(
    (n) => String(n.key) === String(node.key)
  );
  const group =
    draggedIsSelected && selection.length > 1 ? selection : [node];

  const payload = {
    ...dragEntryFromNode(node),
    documents: group.map(dragEntryFromNode),
  };
  event.dataTransfer.setData(DOCUMENT_DRAG_MIME, JSON.stringify(payload));
  // Firefox refuses to start a drag with no well-known type present.
  event.dataTransfer.setData(
    "text/plain",
    group.length > 1 ? `${group.length} files` : payload.documentName
  );
  event.dataTransfer.effectAllowed = "move";
  return true;
};

const readDocumentDragPayload = (event) => {
  try {
    const raw = event?.dataTransfer?.getData?.(DOCUMENT_DRAG_MIME);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

// Resolve which tree row the pointer event landed on. The title span
// carries data-tree-node-key, but a row is wider than its title — the
// switcher arrow, file/folder icon, and indent units live OUTSIDE that
// span inside the same rc-tree row wrapper (.ant-tree-treenode), so fall
// back to the row wrapper's tagged title.
const getRowKeyFromEventTarget = (event) => {
  const target = event?.target;
  if (!target || typeof target.closest !== "function") return null;
  const direct = target.closest("[data-tree-node-key]");
  const host =
    direct ||
    target.closest(".ant-tree-treenode")?.querySelector?.("[data-tree-node-key]");
  const key = host?.getAttribute?.("data-tree-node-key");
  return key != null && key !== "" ? key : null;
};

// Translate a drop event into a move destination, by pointer intent:
// folder row -> into that folder; document row -> into that document's
// containing folder (matter root for top-level documents); unresolvable
// (placeholder/keyless rows, container whitespace) -> undefined.
export const resolveDropDestination = ({ event, treeData }) => {
  const domKey = getRowKeyFromEventTarget(event);
  if (domKey == null) return undefined;
  const domNode = findNodeByKey(treeData, domKey);
  if (!domNode) return undefined;
  return resolveDestinationFromNode(domNode, treeData);
};

// During dragover, compute which FOLDER row should light up as the live
// destination — the same resolution the drop handler uses, so the
// highlight always tells the truth about where the file will land.
// Returns the antd node key of the folder row to highlight, or null
// (matter-root drops / not over a row — nothing highlights).
export const resolveDragoverFolderKey = ({ event, treeData }) => {
  const key = getRowKeyFromEventTarget(event);
  if (key == null) return null;
  const node = findNodeByKey(treeData, key);
  if (!node) return null;
  if (node.type === "folder") return node.key;
  if (node.type === "document") {
    const { found, parent } = findParentFolderNode(treeData, node.key);
    if (found && parent) return parent.key;
  }
  return null;
};

// Container-level dragover. preventDefault is what tells the browser the
// drop is permitted — but ONLY for our internal document drags: desktop
// file drags (uploads) and text selections must keep their default
// behavior. Returns the folder key to highlight (null when none).
export const handleContainerDragOver = ({ event, treeData }) => {
  if (!isDocumentDragEvent(event)) return null;
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "move";
  }
  return resolveDragoverFolderKey({ event, treeData });
};

// Container-level drop. Returns the dispatched promise, or null for
// ignored/no-op drops (foreign drags, unresolvable targets, same-folder).
export const handleContainerDrop = ({ event, dispatch, matterId, treeData }) => {
  if (!isDocumentDragEvent(event)) return null;
  event.preventDefault();

  const payload = readDocumentDragPayload(event);
  if (!payload?.documentId) return null;

  const destination = resolveDropDestination({ event, treeData });
  if (destination === undefined) return null;

  const destinationFolderId = destination.folderId ?? null;

  // Multi-document drags: skip files already in the destination, move the
  // rest as one batch (single summary toast, single refetch, batched
  // Keep-both dialog for conflicts).
  const draggedDocuments = Array.isArray(payload.documents)
    ? payload.documents.filter((d) => d && d.documentId)
    : [];
  if (draggedDocuments.length > 1) {
    const movable = draggedDocuments.filter(
      (d) => String(d.folderId ?? "") !== String(destinationFolderId ?? "")
    );
    if (movable.length === 0) return null;
    return Promise.resolve(
      dispatch(
        performBulkDocumentMove({
          documents: movable.map((d) => ({
            documentId: String(d.documentId),
            documentName: d.documentName || "",
            record: d.record || undefined,
          })),
          destinationFolderId,
          destinationName: destination.folderName,
          matterId,
        })
      )
    );
  }

  // No-op guard, null-safe so root -> root is also skipped.
  if (String(payload.folderId ?? "") === String(destinationFolderId ?? "")) {
    return null;
  }

  const moveArgs = {
    documentId: String(payload.documentId),
    documentName: payload.documentName || "",
    destinationFolderId,
    destinationName: destination.folderName,
    matterId,
    documentRecord: payload.record || undefined,
  };

  // Kept synchronous (returns the dispatch promise, unchanged contract):
  // on a name-collision result, offer "Keep both" (auto-suffix) instead
  // of the old silent bounce (Matthew, 2026-07-21). Confirming re-runs
  // the move with on_conflict="rename".
  return Promise.resolve(dispatch(performDocumentMove(moveArgs))).then(
    (result) => {
      if (result?.conflict) {
        const docLabel = result.documentName || "This file";
        const where = result.destinationName || "that folder";
        Modal.confirm({
          title: "A file with that name is already there",
          icon: <ExclamationCircleOutlined />,
          content: `${docLabel} already exists in ${where}. Keep both — the moved copy will be renamed (e.g. "… (2)") — or cancel and leave it where it is.`,
          okText: "Keep both",
          cancelText: "Cancel",
          onOk: () =>
            dispatch(performDocumentMove({ ...moveArgs, onConflict: "rename" })),
        });
      }
      return result;
    }
  );
};
